<script setup>
/* global axios */
import { computed, onMounted, ref } from 'vue';
import Modal from 'dashboard/components/Modal.vue';
import API from 'dashboard/api/talkviaCreditDashboard';
import CpfReveal from './CpfReveal.vue';
const props = defineProps({
  filter: { type: Object, required: true },
  period: { type: Object, required: true },
});
const emit = defineEmits(['close']);
const show = ref(true);
const items = ref([]);
const page = ref(1);
const total = ref(0);
const loading = ref(false);
const error = ref('');
const conversation = ref(null);
const messages = ref([]);
const conversationUrl = computed(() =>
  conversation.value
    ? `/app/accounts/${conversation.value.account_id}/conversations/${conversation.value.conversation_id}`
    : ''
);
const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    const { data } = await API.records({
      ...props.period,
      source: props.filter.source,
      status: props.filter.status,
      page: page.value,
    });
    items.value = data.items;
    total.value = data.total;
  } catch {
    error.value = 'Não foi possível carregar os registros.';
  } finally {
    loading.value = false;
  }
};
const openConversation = async item => {
  conversation.value = item;
  messages.value = [];
  error.value = '';
  loading.value = true;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${item.account_id}/conversations/${item.conversation_id}/messages`
    );
    messages.value = data.payload || [];
  } catch {
    error.value =
      'Não foi possível abrir a conversa. Verifique seu acesso à caixa de entrada.';
  } finally {
    loading.value = false;
  }
};
const changePage = direction => {
  page.value += direction;
  load();
};
onMounted(() =>
  props.filter.item ? openConversation(props.filter.item) : load()
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <Modal v-model:show="show" @close="emit('close')">
    <div class="flex max-h-[80vh] flex-col p-6">
      <h2 class="mb-3 text-lg font-semibold text-n-slate-12">
        {{
          conversation
            ? `Conversa #${conversation.conversation_id}`
            : filter.label
        }}
      </h2>
      <p v-if="error" role="alert" class="text-n-ruby-11">{{ error }}</p>
      <p v-if="loading" role="status">Carregando...</p>
      <div v-else-if="conversation" class="min-h-0 space-y-3 overflow-y-auto">
        <p class="text-xs text-n-slate-10">
          Mensagens mais recentes. Abra a conversa para consultar o histórico
          completo e os anexos.
        </p>
        <article
          v-for="message in messages"
          :key="message.id"
          class="rounded-lg border border-n-weak p-3"
        >
          <strong class="text-xs">{{
            `${message.sender?.name || 'Sistema'}${message.private ? ' · Nota privada' : ''}`
          }}</strong>
          <p class="mb-0 whitespace-pre-wrap break-words text-sm">
            {{ message.content || 'Mensagem com anexo ou conteúdo interativo' }}
          </p>
        </article>
        <p v-if="!messages.length">Nenhuma mensagem disponível.</p>
      </div>
      <div v-else class="min-h-0 space-y-2 overflow-y-auto">
        <p class="text-xs text-n-slate-10">{{ total }} registros no período</p>
        <article
          v-for="item in items"
          :key="item.record_id"
          class="rounded-lg border border-n-weak p-3"
        >
          <div class="text-sm font-semibold">{{ item.title }}</div>
          <CpfReveal :item="item" :period="period" />
          <button
            v-if="item.conversation_id"
            type="button"
            class="ml-3 text-sm text-n-blue-11"
            @click="openConversation(item)"
          >
            Ver conversa #{{ item.conversation_id }}
          </button>
          <span v-else class="ml-3 text-xs text-n-slate-10" role="status">{{
            'Sem conversa vinculada'
          }}</span>
        </article>
        <p v-if="!items.length">Nenhum registro encontrado.</p>
      </div>
      <div
        class="mt-4 flex shrink-0 items-center justify-between gap-3 border-t border-n-weak pt-3"
      >
        <template v-if="conversation">
          <button
            v-if="!filter.item"
            type="button"
            @click="
              conversation = null;
              error = '';
            "
          >
            Voltar aos registros
          </button>
          <a
            :href="conversationUrl"
            class="text-n-blue-11"
            aria-label="Abrir conversa completa"
            >{{ 'Abrir conversa completa' }}</a
          >
        </template>
        <template v-else>
          <button
            type="button"
            :disabled="page === 1 || loading"
            @click="changePage(-1)"
          >
            Anterior
          </button>
          <span class="text-xs">Página {{ page }}</span>
          <button
            type="button"
            :disabled="page * 20 >= total || loading"
            @click="changePage(1)"
          >
            Próxima
          </button>
        </template>
      </div>
    </div>
  </Modal>
</template>
